import { useState } from 'react';

import {
  Area,
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  LayoutCard,
  Line,
  TimeXAxis,
  ToggleButton,
  ToggleButtonGroup,
  YAxis,
  Stack,
} from '@cast/design-system';
import { dashesOrNum, formatPercentage } from '@cast/utils';

import {
  DashboardChartSummaryMetric,
  DashboardChartSummaryMetricsRow,
} from './DashboardChartCard';
import { DashboardChartFrame } from './DashboardChartFrame';
import { DashboardChartRangePicker } from './DashboardChartRangePicker';
import { DashboardDateChartTooltipHeader } from './DashboardDateChartTooltipHeader';
import { DashboardSectionError } from './DashboardSectionError';
import { DASHBOARD_CHART_TIMEZONE } from '../constants/dashboardCharts';
import { dashboardChartZoomProps } from '../hooks/useDashboardChartZoomState';
import { useDashboardFleetChartColors } from '../hooks/useDashboardFleetChartColors';
import { useDashboardResourceChartData } from '../hooks/useDashboardResourceChartData';
import {
  fmtEstimatedResourceValue,
  fmtEstimatedUtilizationValue,
  getResourceRoundBy,
  getResourceSeriesLabel,
  getResourceUnit,
  type ResourceType,
} from '@cast/features-cluster-shared';
import { clusterColors } from '@cast/features-cluster-ui';

export const ResourcesUtilizationChartSection = () => {
  const fleetChartColors = useDashboardFleetChartColors();
  const [resourceType, setResourceType] = useState<ResourceType>('cpu');
  const { chartData, averages, isLoading, error, refetch } =
    useDashboardResourceChartData({
      resourceType,
    });
  const resourceRoundBy = getResourceRoundBy(resourceType);

  return (
    <LayoutCard
      title="Resources & utilization"
      headerEndContent={
        <Stack direction="row" alignItems="center" gap={8} flexWrap="wrap">
          <DashboardChartRangePicker />
          <ToggleButtonGroup
            size="sm"
            value={resourceType}
            onChange={(_event, value) => {
              if (value) {
                setResourceType(value as ResourceType);
              }
            }}
          >
            <ToggleButton value="cpu" text="CPU" />
            <ToggleButton value="memory" text="Memory" />
            <ToggleButton value="storage" text="Storage" />
          </ToggleButtonGroup>
        </Stack>
      }
    >
      <DashboardSectionError hasError={!!error} onRetry={refetch}>
        <Stack gap={16}>
          <DashboardChartSummaryMetricsRow>
            <DashboardChartSummaryMetric
              color={clusterColors.provisioned}
              label="Avg. provisioned"
              unit={getResourceUnit(resourceType)}
              value={fmtEstimatedResourceValue(
                averages.provisioned,
                resourceRoundBy
              )}
            />
            <DashboardChartSummaryMetric
              color={clusterColors.used}
              label={resourceType === 'storage' ? 'Avg. claimed' : 'Avg. used'}
              unit={getResourceUnit(resourceType)}
              value={fmtEstimatedResourceValue(averages.used, resourceRoundBy)}
            />
            <DashboardChartSummaryMetric
              dashedMarker
              label="Utilization"
              unit="%"
              value={fmtEstimatedUtilizationValue(averages.utilization)}
            />
          </DashboardChartSummaryMetricsRow>

          <DashboardChartFrame>
            <ChartContainer
              data={chartData ?? []}
              isLoading={isLoading}
              hasNoData={!isLoading && !chartData?.length}
              noDataOverlayProps={{ title: 'No utilization data available' }}
              margin={{ left: 0, right: 0, top: 12, bottom: 0 }}
              fallbackChartType="area"
              testId="resources-utilization-chart"
              zoomProps={dashboardChartZoomProps}
              chartActionsSx={{ top: 0, right: 0 }}
            >
              <Area
                stackId={0}
                type="linear"
                dataKey="requestedGap"
                fill={fleetChartColors.requested}
                tooltipConfig={{ displayName: 'Requested buffer' }}
              />
              <Area
                stackId={0}
                type="linear"
                dataKey="used"
                fill={fleetChartColors.used}
                tooltipConfig={{
                  displayName: getResourceSeriesLabel(resourceType),
                }}
              />
              <ChartGrid hideVertical />
              <Line
                dataKey="provisioned"
                stroke={clusterColors.provisioned}
                strokeWidth={1.5}
                dot={false}
                tooltipConfig={{ displayName: 'Provisioned' }}
              />
              <Line
                dataKey="utilization"
                stroke={clusterColors.utilization}
                strokeDasharray="4 4"
                strokeWidth={1.5}
                yAxisId="utilization"
                dot={false}
                tooltipConfig={{ displayName: 'Utilization' }}
              />
              <YAxis
                type="number"
                padding={{ top: 16 }}
                unitFormatter={(value) =>
                  `${dashesOrNum({
                    value: Number(value),
                    roundBy: resourceRoundBy,
                  })}`
                }
              />
              <YAxis
                yAxisId="utilization"
                orientation="right"
                type="number"
                padding={{ top: 16 }}
                unitFormatter={(value) =>
                  formatPercentage(Number(value), { roundBy: 0 })
                }
              />
              <TimeXAxis
                unixDataKey="chartTimestampUnix"
                timezone={DASHBOARD_CHART_TIMEZONE}
              />
              <ChartTooltip<{
                timestamp: string;
                provisioned: number;
                requested: number;
                requestedGap: number;
                used: number;
                utilization: number;
              }>
                header={DashboardDateChartTooltipHeader}
                formatValue={(value, dataKey) =>
                  dataKey === 'utilization'
                    ? formatPercentage(Number(value), { roundBy: 1 })
                    : dashesOrNum({
                        value: Number(value),
                        roundBy: resourceRoundBy,
                      })
                }
              />
            </ChartContainer>
          </DashboardChartFrame>
        </Stack>
      </DashboardSectionError>
    </LayoutCard>
  );
};
